import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { isAxiosError } from 'axios';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { changePasswordRequest } from '../../api/auth';
import { changePasswordSchema, ChangePasswordFormValues } from '../../validation/auth';
import { useAuth } from '../../context/authContext';
import { PasswordStrengthMeter } from '../../components/PasswordStrengthMeter';

export const ChangePasswordPage = () => {
  const { refreshProfile } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({ resolver: zodResolver(changePasswordSchema) });

  const newPassword = watch('newPassword');

  const onSubmit = async (values: ChangePasswordFormValues) => {
    setServerError(null);
    setSuccess(false);
    try {
      await changePasswordRequest(values.currentPassword, values.newPassword);
      setSuccess(true);
      reset();
      // Refreshes the auth context's mustChangePassword flag so a forced
      // redirect (ProtectedRoute) releases immediately instead of trapping
      // the user here until the next full profile refresh.
      await refreshProfile();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 400) {
        setServerError('Current password is incorrect.');
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 480 }}>
      <Card variant="outlined">
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Change password
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 2 }}>
            <Stack spacing={2}>
              {serverError && <Alert severity="error">{serverError}</Alert>}
              {success && <Alert severity="success">Password changed. Other sessions were signed out.</Alert>}

              <TextField
                label="Current password"
                type="password"
                fullWidth
                error={!!errors.currentPassword}
                helperText={errors.currentPassword?.message}
                {...register('currentPassword')}
              />
              <TextField
                label="New password"
                type="password"
                fullWidth
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                {...register('newPassword')}
              />
              <PasswordStrengthMeter password={newPassword ?? ''} />
              <TextField
                label="Confirm new password"
                type="password"
                fullWidth
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />
              <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ alignSelf: 'flex-start' }}>
                {isSubmitting ? 'Saving…' : 'Change password'}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
